import React from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import {
  ArrowSquareOut, ShieldCheck, CurrencyGbp, Lightning,
  BellRinging, ChatCircle, Star, DeviceMobile,
} from "@phosphor-icons/react";

/**
 * MOCKUP PAGE — /mockups/trader-subscribe
 * What a trader would see inside the native iOS/Android app when they need
 * to activate their subscription. NO in-app payment (avoids Apple/Google's
 * 15-30% cut). Instead a friendly "finish setup on the web" screen with an
 * external-browser link.
 */
export default function TraderSubscribeMockup() {
  const openBrowser = () => {
    // In the real native app, this opens the OS's default browser (not the
    // webview) which keeps us on the right side of App Store guideline 3.1.5(a).
    window.open("https://myhandyhub.co.uk/subscribe", "_system");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10"
         style={{ background: "var(--bg-sand)" }}
         data-testid="trader-subscribe-mockup">
      {/* Phone-frame just for the mockup preview — this won't appear in the real app */}
      <div className="w-full max-w-[420px]">
        <div
          className="rounded-[36px] shadow-2xl overflow-hidden border-[10px]"
          style={{ borderColor: "#1a1a1a", background: "#fff" }}
        >
          {/* Faux status bar */}
          <div className="h-6 flex items-center justify-center text-[10px] font-semibold text-neutral-500 bg-white">
            9:41  ·  MyHandyHub
          </div>

          {/* Content */}
          <div className="px-6 pt-6 pb-8">
            <div className="flex items-center justify-center mb-1">
              <Logo size={60} />
            </div>

            <div className="text-center mt-3">
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ background: "#FFF1E5", color: "#7A3A0B" }}
              >
                <Lightning size={11} weight="fill" /> One step to go
              </span>
              <h1 className="font-display font-extrabold text-2xl mt-3 leading-tight"
                  style={{ color: "var(--brand-green)" }}>
                Activate your Trader membership
              </h1>
              <p className="mt-2 text-[13px] text-[#4A5D54] leading-relaxed">
                To start receiving local job alerts, finish setup on the web.
                It takes about 60 seconds.
              </p>
            </div>

            {/* Pricing card (INFORMATIONAL — allowed inside the app) */}
            <div
              className="mt-5 rounded-2xl p-5 border-2 text-center"
              style={{ borderColor: "var(--brand-orange)", background: "#FFF9F3" }}
            >
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-display font-extrabold text-4xl"
                      style={{ color: "var(--brand-green)" }}>£3.99</span>
                <span className="text-[13px] font-semibold text-[#4A5D54]">/month</span>
              </div>
              <div className="text-[11px] font-bold uppercase tracking-widest mt-1"
                   style={{ color: "var(--brand-orange)" }}>
                or £39.99/year (save 17%)
              </div>
              <div className="mt-3 text-[12px] font-semibold" style={{ color: "var(--brand-green)" }}>
                No lead fees · Cancel anytime
              </div>
            </div>

            {/* What you get */}
            <ul className="mt-5 space-y-2.5 text-[13px] text-[#28372F]">
              <li className="flex items-start gap-2">
                <BellRinging size={16} weight="fill" style={{ color: "var(--brand-orange)" }} className="mt-0.5 flex-shrink-0" />
                <span><strong>Instant push alerts</strong> when a job matches your trade &amp; area</span>
              </li>
              <li className="flex items-start gap-2">
                <ChatCircle size={16} weight="fill" style={{ color: "var(--brand-orange)" }} className="mt-0.5 flex-shrink-0" />
                <span><strong>Unlimited quotes &amp; messaging</strong> — no per-lead fees ever</span>
              </li>
              <li className="flex items-start gap-2">
                <Star size={16} weight="fill" style={{ color: "var(--brand-orange)" }} className="mt-0.5 flex-shrink-0" />
                <span><strong>Verified profile</strong> with photos, reviews, ID &amp; insurance badge</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck size={16} weight="fill" style={{ color: "var(--brand-orange)" }} className="mt-0.5 flex-shrink-0" />
                <span><strong>Set your own work range</strong> from 0 to 50 miles</span>
              </li>
            </ul>

            {/* CTA — opens external browser */}
            <button
              type="button"
              onClick={openBrowser}
              className="w-full mt-6 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 hover:-translate-y-0.5 transition-all"
              style={{ background: "var(--brand-green)", color: "#fff" }}
              data-testid="mockup-open-browser-btn"
            >
              Continue in browser
              <ArrowSquareOut size={16} weight="bold" />
            </button>

            <p className="mt-3 text-center text-[11px] text-[#7A8B82]">
              You'll be taken to <strong>myhandyhub.co.uk</strong> to complete secure payment.
              Come back here — your dashboard unlocks automatically.
            </p>

            {/* Already a member link */}
            <div className="mt-6 pt-5 border-t border-[#E9E5D8] text-center">
              <p className="text-[12px] text-[#4A5D54]">
                Already a member?{" "}
                <Link
                  to="/dashboard/trader"
                  className="font-bold underline underline-offset-2"
                  style={{ color: "var(--brand-green)" }}
                  data-testid="mockup-refresh-link"
                >
                  Refresh dashboard
                </Link>
              </p>
            </div>

            {/* Payment logos strip */}
            <div className="mt-6 flex items-center justify-center gap-3 opacity-60">
              <ShieldCheck size={12} weight="fill" style={{ color: "var(--brand-green)" }} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7A8B82]">
                Secure payment · Stripe
              </span>
            </div>
          </div>

          {/* Faux bottom tab bar for context */}
          <div className="border-t border-[#E9E5D8] px-6 py-3 flex items-center justify-around bg-white text-[10px] font-semibold text-neutral-400">
            <span className="flex flex-col items-center gap-0.5"><Lightning size={16} weight="fill" style={{ color: "var(--brand-orange)" }} /><span style={{ color: "var(--brand-orange)" }}>Membership</span></span>
            <span className="flex flex-col items-center gap-0.5"><BellRinging size={16} /><span>Jobs</span></span>
            <span className="flex flex-col items-center gap-0.5"><ChatCircle size={16} /><span>Messages</span></span>
            <span className="flex flex-col items-center gap-0.5"><DeviceMobile size={16} /><span>Profile</span></span>
          </div>
        </div>

        {/* Note under the mockup, only visible on this preview URL */}
        <p className="mt-6 text-center text-xs text-[#7A8B82] max-w-sm mx-auto">
          MOCKUP — this is what a trader would see inside the future iOS/Android app.
          The <strong>"Continue in browser"</strong> button opens their phone's Safari/Chrome
          on myhandyhub.co.uk/subscribe, which keeps 100% of the revenue via Stripe.
        </p>
      </div>
    </div>
  );
}
